/**
 * The cards along the bottom of a job: Notes, Docs, Texts, Emails, Photos,
 * transcripts, storm history, partners and the returning-customer check
 * (ADR 0325). See index.ts for the house style.
 *
 * What a customer, an adjuster or a colleague receives — a note's body, a
 * text, an email, the storm report itself — is data and is not in here.
 */
export const jobPanels: Readonly<Record<string, string>> = {
  // Shared buttons and words
  Add: "Agregar",
  Cancel: "Cancelar",
  Delete: "Eliminar",
  Remove: "Quitar",
  Open: "Abrir",
  "Open ↗": "Abrir ↗",
  Send: "Enviar",
  Search: "Buscar",
  Email: "Email",
  Message: "Mensaje",
  Date: "Fecha",
  State: "Estado",
  Sent: "Enviado",
  Signed: "Firmado",
  Today: "Hoy",
  Yesterday: "Ayer",
  "Saving…": "Guardando…",
  "Sending…": "Enviando…",
  "Loading…": "Cargando…",
  "Searching…": "Buscando…",
  "Remove {name}": "Quitar {name}",
  "this customer": "este cliente",
  "the customer": "el cliente",
  Customer: "Cliente",
  Estimate: "Presupuesto",
  Photos: "Fotos",
  Photo: "Foto",
  Picture: "Foto",
  Pictures: "Fotos",
  Texts: "Mensajes",
  Docs: "Documentos",

  // Notes — Calls & Texts
  "Notes — Calls & Texts": "Notas — Llamadas y mensajes",
  Somebody: "Alguien",
  "Could not attach {files} — the note saved without it.":
    "No se pudo adjuntar {files} — la nota se guardó sin esa foto.",
  "Could not attach {files} — the note saved without them.":
    "No se pudo adjuntar {files} — la nota se guardó sin esas fotos.",
  "Saved. Nobody was emailed — connect your Google account on the Email tab and tags will go out from it.":
    "Guardado. No se envió ningún email — al conectar la cuenta de Google en la pestaña Email, las etiquetas saldrán desde ahí.",
  "Emailed {names}.": "Email enviado a {names}.",
  "Could not email {names}.": "No se pudo enviar email a {names}.",
  "Add a note… type @ to tag someone":
    "Agregar una nota… con @ se etiqueta a alguien",
  "No notes on this job yet": "Aún no hay notas en este trabajo",
  "Notes here stay with this job. The customer page shows every job's notes together. Tag a colleague with @ and they get an email.":
    "Las notas de aquí se quedan con este trabajo. La página del cliente muestra juntas las notas de todos sus trabajos. Al etiquetar a un compañero con @, recibe un email.",
  "Reply… type @ to tag someone": "Responder… con @ se etiqueta a alguien",
  Reply: "Responder",
  NOTE: "NOTA",
  CALL: "LLAMADA",
  TEXT: "MENSAJE",
  EMAIL: "EMAIL",
  Inbound: "Entrante",
  Outbound: "Saliente",
  "no email on file": "sin email registrado",
  "Add photo": "Agregar foto",

  // Docs
  'Delete "{title}"?': '¿Eliminar "{title}"?',
  "The file is removed for good. This cannot be undone.":
    "El archivo se elimina para siempre. Esto no se puede deshacer.",
  "Their copy went to {to}.": "Su copia se envió a {to}.",
  "Their copy is on its way to {to}.": "Su copia va en camino a {to}.",
  "this document": "este documento",
  '{name} has signed "{title}".': '{name} ya firmó "{title}".',
  "Voiding it takes away its force — the signed record is kept, but the estimate stands back down and a new document has to be sent.":
    "Anularlo le quita su validez — el registro firmado se conserva, pero el presupuesto vuelve atrás y hay que enviar un documento nuevo.",
  "Void it anyway?": "¿Anular de todos modos?",
  'Void "{title}"?': '¿Anular "{title}"?',
  "The link stops working for the customer. This cannot be undone — present the estimate again to send a fresh one.":
    "El enlace deja de funcionar para el cliente. Esto no se puede deshacer — para enviar uno nuevo, hay que presentar el presupuesto otra vez.",
  "Uploading…": "Subiendo…",
  "Upload a document": "Subir un documento",
  "Nothing on this job yet": "Aún no hay nada en este trabajo",
  "Everything this job has on paper lands here — what was presented to the customer and what they signed, the measurement report, anything emailed out, and anything you upload.":
    "Todo lo que este trabajo tiene en papel llega aquí — lo que se presentó al cliente y lo que firmó, el informe de medidas, cualquier cosa enviada por email y cualquier cosa que se suba.",
  Document: "Documento",
  "Signed by": "Firmado por",
  "revision {n}": "revisión {n}",
  "Their copy went out {when}. Send it again.":
    "Su copia salió el {when}. Enviarla otra vez.",
  "Email the customer their copy of this agreement":
    "Enviar al cliente por email su copia de este acuerdo",
  "Send copy again": "Enviar copia otra vez",
  "Send their copy": "Enviar su copia",
  "Copy the link the customer opens": "Copiar el enlace que abre el cliente",
  "Copy link": "Copiar enlace",
  "Kill this link so the estimate can be changed or cleared away":
    "Desactivar este enlace para poder cambiar o retirar el presupuesto",
  "Voiding…": "Anulando…",
  Void: "Anular",
  Draft: "Borrador",
  "Sent, not opened": "Enviado, sin abrir",
  Opened: "Abierto",
  Declined: "Rechazado",
  Expired: "Vencido",
  Voided: "Anulado",
  "no copy sent": "sin copia enviada",
  "Change order {n}": "Orden de cambio {n}",
  "Opening…": "Abriendo…",
  "Remove this upload from the job": "Quitar este archivo del trabajo",
  "Emailed {to}": "Enviado por email a {to}",
  // What each kind of file is called (documentKindLabel in packages/core)
  Upload: "Archivo subido",
  "Measurement report": "Informe de medidas",
  "Signed contract": "Contrato firmado",
  Invoice: "Factura",
  "Storm report": "Informe de tormentas",
  "Material order": "Orden de materiales",
  "Supplier receipt": "Recibo del proveedor",
  "Order acknowledgement": "Confirmación de pedido",
  "Supplier invoice": "Factura del proveedor",
  "Credit memo": "Nota de crédito",
  "Supplier email": "Email del proveedor",
  "Expense receipt": "Recibo de gasto",
  "Work order": "Orden de trabajo",
  "Company document": "Documento de la empresa",
  "Crew document": "Documento de cuadrilla",
  "Signed certificate of completion": "Certificado de terminación firmado",

  // Texts
  "This contact has no phone number.":
    "Este contacto no tiene número de teléfono.",
  "This contact has no phone number, so there is nobody to text.":
    "Este contacto no tiene número de teléfono, así que no hay a quién enviarle mensajes.",
  "A text can carry 5 pictures; that is the most.":
    "Un mensaje lleva hasta 5 fotos; ese es el máximo.",
  "Pictures go from the Roof Board line. Switch the sender, or take the pictures off.":
    "Las fotos salen desde la línea de Roof Board. Cambiar el remitente o quitar las fotos.",
  "Stop syncing texts with this person, and remove the ones already here?":
    "¿Dejar de sincronizar los mensajes con esta persona y quitar los que ya están aquí?",
  "Remove this text from the customer's profile?":
    "¿Quitar este mensaje del perfil del cliente?",
  "with {name}": "con {name}",
  "Texts from your own phone with this person are not being synced.":
    "Los mensajes del teléfono propio con esta persona no se están sincronizando.",
  "Texts from your own phone with this person are synced here.":
    "Los mensajes del teléfono propio con esta persona se sincronizan aquí.",
  "Start syncing them": "Empezar a sincronizarlos",
  "Stop syncing them": "Dejar de sincronizarlos",
  "Nothing yet. A text sent from here goes out from the Roof Board line, so their reply comes back to this conversation.":
    "Aún nada. Un mensaje enviado desde aquí sale por la línea de Roof Board, así que su respuesta vuelve a esta conversación.",
  "My phone": "Mi teléfono",
  "Their phone": "Su teléfono",
  "A rep's phone": "Teléfono de un vendedor",
  From: "De",
  "From line": "Línea de salida",
  "Text {number}…": "Mensaje a {number}…",
  "Pictures to send": "Fotos por enviar",
  "Pictures go from the Roof Board line":
    "Las fotos salen desde la línea de Roof Board",
  "Add a picture to this text": "Agregar una foto a este mensaje",
  "+ Picture": "+ Foto",
  "{characters} characters · {segments} texts":
    "{characters} caracteres · {segments} mensajes",
  "{n} characters": "{n} caracteres",
  "emoji shorten a text": "los emoji acortan un mensaje",
  "Open Messages": "Abrir Mensajes",
  "Sent in a text": "Enviada en un mensaje",
  "from {line}": "desde {line}",
  sending: "enviando",
  sent: "enviado",
  // Why a text did not arrive (describeTextFailure in packages/core)
  "They replied STOP, so this number cannot be texted again unless they text START.":
    "Respondieron STOP, así que no se le pueden enviar mensajes a este número a menos que envíen START.",
  "That is not a number that can receive a text — it may be a landline.":
    "Ese número no puede recibir mensajes — puede ser una línea fija.",
  "Twilio would not carry a message to that number.":
    "Twilio no quiso llevar un mensaje a ese número.",
  "Their phone was unreachable — switched off, or out of coverage.":
    "Su teléfono no estaba disponible — apagado o sin cobertura.",
  "Their phone blocked the message.": "Su teléfono bloqueó el mensaje.",
  "That number does not exist any more.": "Ese número ya no existe.",
  "That is a landline, or a carrier that does not take texts.":
    "Es una línea fija, o una compañía que no recibe mensajes.",
  "Their carrier filtered the message as spam. A shorter message without links usually gets through.":
    "Su compañía filtró el mensaje como spam. Un mensaje más corto y sin enlaces suele pasar.",
  "The carrier would not say why it failed. Try once more, then ring them.":
    "La compañía no dijo por qué falló. Intentar una vez más y después llamarles.",
  "The message did not arrive.": "El mensaje no llegó.",
  // Which number it leaves from (TEXT_FROM_LABEL / TEXT_FROM_NOTE)
  "Send from": "Enviar desde",
  "Roof Board number": "Número de Roof Board",
  "My cell": "Mi celular",
  "Sent by Roof Board. Their reply comes back here, where anybody can answer it.":
    "Enviado por Roof Board. Su respuesta vuelve aquí, donde cualquiera puede contestarla.",
  "Opens Messages on your phone with the text ready — you tap send. Their reply goes to your phone, and both show up here within a minute.":
    "Abre Mensajes en el teléfono con el texto listo — solo falta tocar enviar. Su respuesta llega al teléfono, y ambos aparecen aquí en menos de un minuto.",
  "Messages will open to {number}.": "Mensajes se abrirá con {number}.",

  // Transcripts
  "Added — but only part of it came across. Open it in Plaud to check what was shared.":
    "Agregado — pero solo llegó una parte. Abrirlo en Plaud para revisar qué se compartió.",
  "Added from Plaud.": "Agregado desde Plaud.",
  "Added.": "Agregado.",
  "Added {name} — but there was not much in it. Check the file.":
    "Se agregó {name} — pero traía poco contenido. Revisar el archivo.",
  "Added {name}.": "Se agregó {name}.",
  "Add a transcript": "Agregar una transcripción",
  "Upload the transcript file, or paste the words in. A Plaud share link works too — open the recording in Plaud, press Share, and paste the link.":
    "Subir el archivo de la transcripción, o pegar el texto. Un enlace compartido de Plaud también sirve — abrir la grabación en Plaud, tocar Compartir y pegar el enlace.",
  "…or paste the transcript, or a Plaud share link":
    "…o pegar la transcripción, o un enlace compartido de Plaud",
  "Reading…": "Leyendo…",
  "Add transcript": "Agregar transcripción",
  "No transcripts yet": "Aún no hay transcripciones",
  "Nothing has been added to this job.":
    "No se ha agregado nada a este trabajo.",
  "Ask about this job": "Preguntar sobre este trabajo",
  "Answered only from the transcripts above. If they do not say, it says so rather than guessing — what comes back here gets repeated to a customer.":
    "Responde solo con las transcripciones de arriba. Si no lo dicen, lo indica en lugar de adivinar — lo que sale aquí se le repite a un cliente.",
  "What did we tell them about the gutters?":
    "¿Qué les dijimos sobre los canalones?",
  "Reading the transcripts…": "Leyendo las transcripciones…",
  Ask: "Preguntar",
  "Type a question first.": "Primero hay que escribir una pregunta.",
  "That is longer than a question. Ask one thing at a time.":
    "Eso es más largo que una pregunta. Preguntar una cosa a la vez.",
  "There is no transcript on this job yet. Add one above, then ask.":
    "Este trabajo aún no tiene transcripción. Agregar una arriba y después preguntar.",
  Answer: "Respuesta",
  Recording: "Grabación",
  "Auto-matched — verify": "Asignado automáticamente — verificar",
  "More than one job fits — pick one":
    "Más de un trabajo coincide — elegir uno",
  "No job found — assign it": "No se encontró trabajo — asignarlo",
  "Too short to match": "Demasiado corta para asignar",
  Summary: "Resumen",
  "No summary came with this recording.": "Esta grabación no trajo resumen.",
  Transcript: "Transcripción",
  "No transcript came with this recording.":
    "Esta grabación no trajo transcripción.",
  "Open in Plaud": "Abrir en Plaud",
  "under a minute": "menos de un minuto",
  "{n} min": "{n} min",

  // Emails
  "Send as": "Enviar como",
  "Gmail not connected": "Gmail no conectado",
  "This customer has no email address. Add one under Overview and try again.":
    "Este cliente no tiene email. Agregar uno en Resumen e intentar de nuevo.",
  "Write an email": "Escribir un email",
  History: "Historial",
  "Nothing has been emailed about this job.":
    "No se ha enviado ningún email sobre este trabajo.",
  "Email {name}": "Email a {name}",

  // Photos (CompanyCam)
  "Joined the project already at this address.":
    "Se unió al proyecto que ya existía en esta dirección.",
  "Created the project in CompanyCam.": "Se creó el proyecto en CompanyCam.",
  "CompanyCam project on its way.": "Proyecto de CompanyCam en camino.",
  "Photos are in CompanyCam.": "Las fotos están en CompanyCam.",
  "CompanyCam not linked — retrying.":
    "CompanyCam no vinculado — reintentando.",
  "CompanyCam gave up after 5 tries.":
    "CompanyCam se rindió después de 5 intentos.",
  "This job has no CompanyCam project yet.":
    "Este trabajo aún no tiene proyecto de CompanyCam.",
  "Open this job in CompanyCam": "Abrir este trabajo en CompanyCam",
  "Open CompanyCam": "Abrir CompanyCam",
  "Working…": "Trabajando…",
  "Create / Link CompanyCam Project": "Crear / vincular proyecto de CompanyCam",
  "Joined a project that was already at this address":
    "Se unió a un proyecto que ya existía en esta dirección",
  "Created for this job": "Creado para este trabajo",
  "No project yet": "Aún no hay proyecto",
  "Every job gets a CompanyCam project as soon as it is created — or joins the one already at that address, so a repeat customer's photos stay together. If CompanyCam was unreachable, this comes back by itself; the button forces it.":
    "Cada trabajo recibe un proyecto de CompanyCam en cuanto se crea — o se une al que ya existe en esa dirección, para que las fotos de un cliente repetido queden juntas. Si CompanyCam no estaba disponible, esto se reintenta solo; el botón lo fuerza.",

  // Storm history
  "Storm history": "Historial de tormentas",
  "No address on this job": "Este trabajo no tiene dirección",
  "This job has no address on it yet. Add one on the customer file and the storm history can be pulled.":
    "Este trabajo aún no tiene dirección. Al agregar una en la ficha del cliente, se puede consultar el historial de tormentas.",
  "Every hail and wind report the National Weather Service holds near {address}, written up with a link beside each one so an adjuster can check it. Nothing on it names us.":
    "Cada reporte de granizo y viento que el Servicio Meteorológico Nacional tiene cerca de {address}, redactado con un enlace junto a cada uno para que un ajustador pueda verificarlo. Nada en él nos menciona.",
  "Pull Storm Report": "Consultar reporte de tormentas",
  Pulled: "Consultado",
  Searched: "Búsqueda",
  Found: "Encontrado",
  "Adjuster opened": "Abierto por el ajustador",
  "Pulled, not sent": "Consultado, sin enviar",
  "Link killed": "Enlace desactivado",
  '{years} yr · {inches}" hail {miles} mi':
    '{years} años · granizo de {inches}" a {miles} mi',
  "no wind": "sin viento",
  "wind {miles} mi": "viento a {miles} mi",
  Nothing: "Nada",
  "1 report": "1 reporte",
  "{n} reports": "{n} reportes",
  "{n} times": "{n} veces",
  "Kill the link. The record of what was sent is kept.":
    "Desactivar el enlace. El registro de lo enviado se conserva.",
  "Kill link": "Desactivar enlace",
  Discard: "Descartar",
  "Keep this report": "Guardar este reporte",
  "Years back": "Años atrás",
  "how far back to look": "cuánto tiempo atrás buscar",
  "Hail size": "Tamaño del granizo",
  "inches and bigger": "pulgadas o más",
  "Hail: miles around": "Granizo: millas a la redonda",
  "from the property": "desde la propiedad",
  "Wind: miles around": "Viento: millas a la redonda",
  "kept tighter": "más acotado",
  "The defaults are ten years, five miles and three-quarter-inch hail. Widen it if a tight search comes back empty — whatever you use is printed on the report, so an adjuster can see the search was not tuned afterwards to produce a result.":
    "Los valores por defecto son diez años, cinco millas y granizo de tres cuartos de pulgada. Ampliarlos si una búsqueda ajustada no devuelve nada — lo que se use queda impreso en el reporte, para que un ajustador vea que la búsqueda no se ajustó después para producir un resultado.",
  "Nothing found": "No se encontró nada",
  'No hail at or above {inches}" was reported within {miles} miles in the last {years} years. That is a real answer, not a failure — hail bands are narrow and a report only exists where somebody called one in. You can widen the search, or keep this as a record that the search was done.':
    'No se reportó granizo de {inches}" o más en {miles} millas a la redonda en los últimos {years} años. Es una respuesta real, no un fallo — las franjas de granizo son angostas y un reporte solo existe donde alguien lo avisó. Se puede ampliar la búsqueda, o guardar esto como constancia de que la búsqueda se hizo.',
  "1 report found · likely date of loss {day}":
    "1 reporte encontrado · fecha probable del siniestro {day}",
  "{n} reports found · likely date of loss {day}":
    "{n} reportes encontrados · fecha probable del siniestro {day}",
  "The strongest one is {lead}, {miles} miles {bearing} of the house.":
    "El más fuerte es {lead}, a {miles} millas al {bearing} de la casa.",
  "Read this before you send it": "Leer esto antes de enviarlo",
  "{day} produced {n} separate reports near this house.":
    "El {day} produjo {n} reportes distintos cerca de esta casa.",
  "{n} separate days produced four or more reports each near this house.":
    "{n} días distintos produjeron cuatro o más reportes cada uno cerca de esta casa.",
  "If the roof was already on then, that is earlier hail the roof has already taken — useful context, but an adjuster can also use it to argue the roof is simply old. Your call whether it helps.":
    "Si el techo ya estaba puesto entonces, eso es granizo anterior que el techo ya recibió — contexto útil, pero un ajustador también puede usarlo para argumentar que el techo simplemente es viejo. Queda a criterio si ayuda.",
  "Look at the report first ↗": "Ver el reporte primero ↗",
  '{inches}" hail, {how}': 'granizo de {inches}", {how}',
  "physically measured by a National Weather Service employee — the best evidence this data holds":
    "medido físicamente por un empleado del Servicio Meteorológico Nacional — la mejor evidencia que tienen estos datos",
  "estimated by whoever reported it, not measured":
    "estimado por quien lo reportó, no medido",
  "a {mph} mph wind gust": "una ráfaga de viento de {mph} mph",
  "reported wind damage, with no wind speed recorded":
    "daño por viento reportado, sin velocidad registrada",
  "Email the storm report": "Enviar el reporte de tormentas por email",
  "Goes out as a link and as an attached copy.":
    "Sale como enlace y como copia adjunta.",
  To: "Para",
  Subject: "Asunto",

  // Partners
  "Referred by a partner?": "¿Referido por un socio?",
  "Nobody recorded yet. If a realtor, an agent or an adjuster sent this one our way, say so — it is how they find out it turned into a roof.":
    "Aún no hay nadie registrado. Si un agente inmobiliario, un agente de seguros o un ajustador nos mandó este, hay que anotarlo — así se enteran de que terminó en un techo.",
  "→ us": "→ nosotros",
  "→ them": "→ ellos",
  Realtor: "Agente inmobiliario",
  "Insurance agent": "Agente de seguros",
  Adjuster: "Ajustador",
  "Property manager": "Administrador de propiedades",
  "HOA board": "Junta de la HOA",
  "Mortgage lender": "Prestamista hipotecario",
  "Home inspector": "Inspector de viviendas",
  "Trade / contractor": "Oficio / contratista",
  "Networking contact": "Contacto de networking",
  Other: "Otro",
  open: "abierto",
  contacted: "contactado",
  won: "ganado",
  lost: "perdido",
  "Who sent them": "Quién los mandó",
  "Pick a partner": "Elegir un socio",
  "Anything worth remembering": "Algo que valga la pena recordar",
  "Called about the inspection report on the listing":
    "Llamó por el informe de inspección de la propiedad en venta",
  "Record it": "Registrarlo",
  "Add another": "Agregar otro",
  "Yes — record it": "Sí — registrarlo",
  "Refer this customer to a partner": "Referir este cliente a un socio",
  "A realtor, a lender, another trade. Opens Messages with the introduction ready to go from your own phone.":
    "Un agente inmobiliario, un prestamista, otro oficio. Abre Mensajes con la presentación lista para salir desde el teléfono propio.",
  "Send them to": "Mandarlos con",
  "no mobile": "sin celular",

  // Possible returning customer
  "Link this job to {name}? The form's phone, email, and name will NOT replace their customer details.":
    "¿Vincular este trabajo a {name}? El teléfono, email y nombre del formulario NO reemplazarán sus datos de cliente.",
  "Possible returning customer": "Posible cliente que regresa",
  "This public form made a separate customer file. Check the person's identity before linking this job. Submitted contact details stay in the original lead record and will not change the existing customer.":
    "Este formulario público creó una ficha de cliente aparte. Verificar la identidad de la persona antes de vincular este trabajo. Los datos de contacto enviados se quedan en el registro original del prospecto y no cambiarán al cliente existente.",
  "Possible existing customer": "Posible cliente existente",
  "Choose a customer after checking": "Elegir un cliente después de verificar",
  "No details": "Sin datos",
  "Linking…": "Vinculando…",
  "Confirm and link job": "Confirmar y vincular trabajo",
};
